/**
 * ActivatePage — 로그인 연동(초기 계정 활성화) — cycle 96.
 *
 * 아직 연동 안 된 계정을 HR/관리자 발급 (회사코드 + 이메일 + 사원번호 + 임시비밀번호) 로 1회 연동하고
 * 본인의 새 비밀번호를 설정한다. 이후에는 이메일 + 비밀번호로 로그인한다.
 * 백엔드: POST /api/v1/auth/activate-account (3-factor + HR 연동 시 사원번호 실재 검증, cycle 91/93).
 *
 * i18n: LoginPage 와 동일하게 page-local dict + getSharedLang (lib/*-i18n 완결성 검사 대상 아님).
 */
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { apiClient } from '../../api/client';
import { getSharedLang, type Lang } from '../../lib/base-i18n';
import { AtSign, Building2, Eye, EyeOff, Hash, Key, Loader2, Lock } from '../../icons';
import '../LoginPage/LoginPage.css';

const ACTIVATE_MESSAGES: Record<Lang, {
  heading: string; subheading: string;
  label_company_code: string; label_email: string; label_employee_number: string;
  label_temp_password: string; label_new_password: string; label_confirm_password: string;
  ph_company_code: string; ph_temp_password: string; ph_new_password: string;
  hint_company_code: string;
  btn_submit: string; back_to_login: string;
  aria_show_password: string; aria_hide_password: string;
  success_heading: string; success_body: string; go_login: string;
  err_invalid: string; err_policy: string; err_mismatch: string; err_generic: string;
}> = {
  ko: {
    heading: '로그인 연동',
    subheading: '회사에서 발급받은 정보로 계정을 연동하세요. 연동 후에는 이메일과 비밀번호로 로그인합니다.',
    label_company_code: '회사 코드',
    label_email: '이메일',
    label_employee_number: '사원번호',
    label_temp_password: '초기(임시) 비밀번호',
    label_new_password: '새 비밀번호',
    label_confirm_password: '새 비밀번호 확인',
    ph_company_code: '회사 코드 (도메인 자동 식별 시 생략 가능)',
    ph_temp_password: '회사에서 발급한 임시 비밀번호',
    ph_new_password: '8자 이상 새 비밀번호',
    hint_company_code: '이메일 도메인으로 회사가 식별되면 비워두어도 됩니다.',
    btn_submit: '연동하고 비밀번호 설정',
    back_to_login: '로그인으로 돌아가기',
    aria_show_password: '비밀번호 표시',
    aria_hide_password: '비밀번호 숨기기',
    success_heading: '연동 완료',
    success_body: '비밀번호가 설정되었습니다. 이제 이메일과 새 비밀번호로 로그인하세요.',
    go_login: '로그인하러 가기',
    err_invalid: '등록 정보가 올바르지 않습니다 (회사 코드·이메일·사원번호·임시 비밀번호 확인).',
    err_policy: '새 비밀번호가 정책을 만족하지 않습니다 (8자 이상).',
    err_mismatch: '새 비밀번호가 일치하지 않습니다.',
    err_generic: '연동에 실패했습니다. 잠시 후 다시 시도하세요.',
  },
  en: {
    heading: 'Account linking',
    subheading: 'Link your account with the info issued by your company. After linking, sign in with email and password.',
    label_company_code: 'Company code',
    label_email: 'Email',
    label_employee_number: 'Employee number',
    label_temp_password: 'Initial (temporary) password',
    label_new_password: 'New password',
    label_confirm_password: 'Confirm new password',
    ph_company_code: 'Company code (optional if domain resolves)',
    ph_temp_password: 'Temporary password from your company',
    ph_new_password: 'New password (8+ chars)',
    hint_company_code: 'Leave blank if your company is identified by email domain.',
    btn_submit: 'Link and set password',
    back_to_login: 'Back to sign in',
    aria_show_password: 'Show password',
    aria_hide_password: 'Hide password',
    success_heading: 'Linked',
    success_body: 'Your password is set. Now sign in with your email and new password.',
    go_login: 'Go to sign in',
    err_invalid: 'Invalid registration info (check company code, email, employee number, temporary password).',
    err_policy: 'New password does not meet the policy (8+ chars).',
    err_mismatch: 'New passwords do not match.',
    err_generic: 'Linking failed. Please try again shortly.',
  },
  ja: {
    heading: 'ログイン連動',
    subheading: '会社から発行された情報でアカウントを連動してください。連動後はメールとパスワードでログインします。',
    label_company_code: '会社コード',
    label_email: 'メール',
    label_employee_number: '社員番号',
    label_temp_password: '初期（仮）パスワード',
    label_new_password: '新しいパスワード',
    label_confirm_password: '新しいパスワード（確認）',
    ph_company_code: '会社コード（ドメイン自動識別時は省略可）',
    ph_temp_password: '会社が発行した仮パスワード',
    ph_new_password: '8文字以上の新しいパスワード',
    hint_company_code: 'メールのドメインで会社が識別される場合は空欄で構いません。',
    btn_submit: '連動してパスワード設定',
    back_to_login: 'ログインに戻る',
    aria_show_password: 'パスワードを表示',
    aria_hide_password: 'パスワードを非表示',
    success_heading: '連動完了',
    success_body: 'パスワードが設定されました。メールと新しいパスワードでログインしてください。',
    go_login: 'ログインへ',
    err_invalid: '登録情報が正しくありません（会社コード・メール・社員番号・仮パスワードを確認）。',
    err_policy: '新しいパスワードがポリシーを満たしません（8文字以上）。',
    err_mismatch: '新しいパスワードが一致しません。',
    err_generic: '連動に失敗しました。しばらくしてから再試行してください。',
  },
};

function tAct(): typeof ACTIVATE_MESSAGES['ko'] {
  return ACTIVATE_MESSAGES[getSharedLang()] ?? ACTIVATE_MESSAGES.en;
}

interface ApiError { code?: string; message?: string }

export function ActivatePage() {
  const navigate = useNavigate();
  const [companyCode, setCompanyCode] = useState('');
  const [email, setEmail] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const t = tAct();

  const isValid =
    email.trim().length > 0 &&
    employeeNumber.trim().length > 0 &&
    tempPassword.length > 0 &&
    newPassword.length >= 8 &&
    confirmPassword.length > 0;

  async function handleSubmit(e: FormEvent | KeyboardEvent): Promise<void> {
    if ('preventDefault' in e) e.preventDefault();
    if (!isValid || submitting) return;
    if (newPassword !== confirmPassword) {
      setErrorMessage(t.err_mismatch);
      return;
    }
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await apiClient.post('/auth/activate-account', {
        email: email.trim(),
        employeeNumber: employeeNumber.trim(),
        tempPassword,
        newPassword,
        ...(companyCode.trim() ? { institutionCode: companyCode.trim() } : {}),
      });
      setDone(true);
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      const code = ax.response?.data?.code;
      setErrorMessage(
        code === 'INVALID_ACTIVATION'
          ? t.err_invalid
          : code === 'PASSWORD_POLICY'
            ? t.err_policy
            : ax.response?.data?.message ?? t.err_generic,
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleEnter(ev: KeyboardEvent<HTMLInputElement>): void {
    if (ev.key === 'Enter' && isValid && !submitting) void handleSubmit(ev);
  }

  return (
    <div className="login-page" style={{ justifyContent: 'center' }}>
      <section className="login-panel">
        <div className="login-panel__inner">
          {done ? (
            <div role="status">
              <h2 className="login-panel__heading">{t.success_heading}</h2>
              <p className="login-panel__subheading">{t.success_body}</p>
              <button
                type="button"
                className="login-form__button login-form__button--primary"
                onClick={() => navigate('/login', { replace: true })}
              >
                {t.go_login}
              </button>
            </div>
          ) : (
            <>
              <h2 className="login-panel__heading">{t.heading}</h2>
              <p className="login-panel__subheading">{t.subheading}</p>

              {errorMessage && (
                <div role="alert" className="login-form__error"><span>{errorMessage}</span></div>
              )}

              <form onSubmit={(e) => void handleSubmit(e)} noValidate>
                <div className="login-form__field">
                  <label htmlFor="act-email" className="login-form__label">{t.label_email}</label>
                  <div className="login-form__input-wrapper">
                    <AtSign size={16} className="login-form__icon-left" />
                    <input id="act-email" type="email" autoComplete="username" className="login-form__input"
                      value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={handleEnter}
                      placeholder="name@company.com" required />
                  </div>
                </div>

                <div className="login-form__field">
                  <label htmlFor="act-empno" className="login-form__label">{t.label_employee_number}</label>
                  <div className="login-form__input-wrapper">
                    <Hash size={16} className="login-form__icon-left" />
                    <input id="act-empno" type="text" className="login-form__input"
                      value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} onKeyDown={handleEnter}
                      placeholder="E1001" maxLength={32} required />
                  </div>
                </div>

                <div className="login-form__field">
                  <label htmlFor="act-temp" className="login-form__label">{t.label_temp_password}</label>
                  <div className="login-form__input-wrapper">
                    <Key size={16} className="login-form__icon-left" />
                    <input id="act-temp" type="password" autoComplete="off" className="login-form__input"
                      value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} onKeyDown={handleEnter}
                      placeholder={t.ph_temp_password} required />
                  </div>
                </div>

                <div className="login-form__field">
                  <label htmlFor="act-new" className="login-form__label">{t.label_new_password}</label>
                  <div className="login-form__input-wrapper">
                    <Lock size={16} className="login-form__icon-left" />
                    <input id="act-new" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                      className="login-form__input login-form__input--with-action"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} onKeyDown={handleEnter}
                      placeholder={t.ph_new_password} minLength={8} required />
                    <button type="button" className="login-form__icon-right" onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t.aria_hide_password : t.aria_show_password}
                      title={showPassword ? t.aria_hide_password : t.aria_show_password}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="login-form__field">
                  <label htmlFor="act-confirm" className="login-form__label">{t.label_confirm_password}</label>
                  <div className="login-form__input-wrapper">
                    <Lock size={16} className="login-form__icon-left" />
                    <input id="act-confirm" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                      className="login-form__input"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onKeyDown={handleEnter}
                      placeholder={t.label_confirm_password} minLength={8} required />
                  </div>
                </div>

                <div className="login-form__field">
                  <label htmlFor="act-code" className="login-form__label">{t.label_company_code}</label>
                  <div className="login-form__input-wrapper">
                    <Building2 size={16} className="login-form__icon-left" />
                    <input id="act-code" type="text" autoComplete="organization" className="login-form__input"
                      value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} onKeyDown={handleEnter}
                      placeholder={t.ph_company_code} maxLength={50} />
                  </div>
                  <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                    {t.hint_company_code}
                  </span>
                </div>

                <button type="submit" className="login-form__button login-form__button--primary"
                  disabled={!isValid || submitting} aria-busy={submitting}>
                  {submitting ? (<><Loader2 size={16} className="login-form__spinner" /> …</>) : t.btn_submit}
                </button>
              </form>

              <Link to="/login" className="login-form__company-code-toggle">{t.back_to_login}</Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
